import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {loadModules} from 'esri-loader';

import {MapService} from '../../services/map/map.service';
import {ConfigService} from '../../services/config/config.service';
import {GoogleAnalyticsService} from '../../services/googleAnalytics/google-analytics.service';
import {IdentifyService} from '../../services/identify/identify.service';
import esri = __esri;
import { watch } from 'esri/core/watchUtils';

/**
 * Creates the 2D map view object after the map object is created and adds all of the basic esri widgets to the screen
 */
@Component({
  selector: 'app-esri-map-view',
  templateUrl: './esri-map-view.component.html',
  styleUrls: ['./esri-map-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EsriMapViewComponent implements OnInit, OnDestroy {

  @ViewChild('mapViewNode', {static: true}) private mapViewEl: ElementRef;

  private mapView;
  private searchWidget;

  constructor(private map: MapService, private config: ConfigService, private cd: ChangeDetectorRef,
              private ga: GoogleAnalyticsService, private identify: IdentifyService) { }

  /**
   * Initializes the map view creation when the page loads
   */
  public async ngOnInit() {
    const [watchUtils] = await loadModules([
      'esri/core/watchUtils'
    ]);
    const loading = document.querySelector('#loading-svg') as HTMLElement;
    this.map.getmapLoadedObservable().subscribe((map) => {
      if (map) {
        this.initializeMap(map).then(
          (mapView) => {
            let flag = false;
            if (this.map.getMapViewAr().length > 1) {
              for (const view of this.map.getMapViewAr()) {
                if (view.name === '2d') {
                  mapView = view;
                  flag = true;
                  break;
                }
              }
            }
            this.mapView = mapView;
            this.mapView.when(async () => {
              await watchUtils.whenFalse(this.mapView, 'updating', () => {
                loading.style.visibility = 'hidden';
              });
              await watchUtils.whenTrue(this.mapView, 'updating', () => {
                loading.style.visibility = 'unset';
              });
            });
            this.map.setMapType(2);
            this.map.setViewRef(mapView, flag);
            this.setupEsriWidgets(mapView);
            this.map.setupPreviousExtentButton();
            this.map.setViewRefObservable(true);
            this.identify.identifyInit();
            this.cd.detectChanges();
          });
      }
    }, (error) => {
      this.ga.exception(error,  true);
    });
  }

  /**
   * Makes sure that when the route changes, that the user doesn't lose their location on the map
   */
  public ngOnDestroy(): void {
    if (this.map && this.mapView) {
      this.map.setZoom(this.mapView.zoom);
      this.map.setScale(this.mapView.scale);
      this.map.setRotation(this.mapView.rotation);
      this.map.setCenter(this.mapView.center);
      //this.map.destroyViewRef();
      this.mapView = null;
      this.map.setViewRefObservable(false);
    }
  }

  /**
   * This initializes the mapview on top of the map object
   * @param map: Map object created in the map service
   */
  private async initializeMap(map) {
    try {
      const [MapView] = await loadModules([
        'esri/views/MapView'
      ]);
      this.map.setMapViewGroundLayer();
      const mapViewProperties = {
        container: this.mapViewEl.nativeElement,
        center: this.map.getCenter(),
        zoom: this.map.getZoom(),
        map,
        name: '2d'
      };
      this.cd.detectChanges();
      return new MapView(mapViewProperties);
    } catch (error) {
      this.ga.exception(error, true);
    }
  }

  /**
   * Sets up the Esri widgets that show up on the map
   * Compass/Home/Locate/Scalebar/Search
   * @param mapView: the mapview object to attach the widgets to
   */
  private async setupEsriWidgets(mapView) {
    try {
      const [Compass, Home, Locate, ScaleBar, Search, Zoom] = await loadModules([
        'esri/widgets/Compass',
        'esri/widgets/Home',
        'esri/widgets/Locate',
        'esri/widgets/ScaleBar',
        'esri/widgets/Search',
        'esri/widgets/Zoom'
      ]);
      mapView.ui.remove('zoom');
      const compass = new Compass({
        view: mapView,
        container: 'compass'
      });

      const homeBtn = new Home({
        view: mapView,
        container: 'home'
      });

      const locateBtn = new Locate({
        view: mapView,
        container: 'locate'
      });

      const zoomBtn = new Zoom({
        view: mapView,
        container: 'zoom'
      });

      const scaleBar = new ScaleBar({
        view: mapView,
        unit: 'dual'
      });
      mapView.ui.add(scaleBar, 'bottom-left');

      // Resets view ref of search widget
      if (document.querySelector('#searchContainer') &&
        !document.querySelector('#searchContainer').innerHTML) {
        const sources = [];
        const searchProperties: any = {
          view: mapView,
          container: 'searchContainer',
        };
        if (this.config.search.length > 0) {
          for (const layer of this.config.search) {
            sources.push(await this.map.createSearchLayers(layer));
          }
          searchProperties.sources = sources;
        }
        this.searchWidget = new Search(searchProperties);
        this.mapView.ui.add(mapView.searchWidget, 'top-left');
        this.map.setSearchWidget(this.searchWidget);
      }
      this.map.enableIdentify();
      this.cd.detectChanges();
    } catch (error) {
      this.ga.exception(error, false);
    }
  }
}
